import {useCallback, useReducer, useState} from 'react';

import {CustomFields} from '../../../../settings/editors/commons/customFieldsModel';
import {ExpressionAction} from '../../../../settings/editors/expressions/expressionActions';
import {PreExpression} from '../../../../settings/editors/expressions/expressionModel';
import {expressionReducer} from '../../../../settings/editors/expressions/expressionReducer';
import {createExpressionsContext} from '../../../../settings/editors/expressions/expressionsContext';
import {
  ExpressionState,
  ExpressionStateActions,
  useExpressionState,
} from '../../../../settings/editors/expressions/expressionState';
import {ChatBotResult} from '../../../../settings/editors/expressions/infer/expressionResultModel';
import {ChatBotExpressionsEditor} from './expressionHelpers';

/*
 * Hooks.
 */

export function useChatBotExpressionsEditor(
  initialExpressions: ReadonlyArray<PreExpression>,
  customFields: CustomFields,
): ChatBotExpressionsEditor {
  const {state, actions} = useChatBotExpressionsEditorState(initialExpressions, customFields);
  const context = useExpressionEditorContext(state, actions);

  const openCreateExpression = useCallback(() => actions.onSetPanelOpen(true), [actions]);

  const [isExpressionListPanelVisible, setIsExpressionListPanelVisible] = useState(false);
  const openExpressionList = () => setIsExpressionListPanelVisible(true);
  const closeExpressionList = () => setIsExpressionListPanelVisible(false);

  return {
    state,
    actions,
    context,
    openCreateExpression,
    isExpressionListPanelVisible,
    openExpressionList,
    closeExpressionList,
  };
}

function useChatBotExpressionsEditorState(
  preExpressions: ReadonlyArray<PreExpression>,
  customFields: CustomFields,
) {
  const chatBotExpressionReducer = (state: ExpressionState, action: ExpressionAction<any>) =>
    expressionReducer(state, action, {customFields: state.customFields});

  const [state, dispatch] = useReducer(
    chatBotExpressionReducer,
    ExpressionState.createInitialState(preExpressions, ChatBotResult, {customFields}),
  );

  const actions = useExpressionState({}, state, dispatch);

  return {state, actions};
}

function useExpressionEditorContext(state: ExpressionState, actions: ExpressionStateActions) {
  return createExpressionsContext({
    expressions: state.expressions,
    dependencies: {customFields: state.customFields},
    initialInfer: ChatBotResult,
    setPanelOpen: () => actions.onSetPanelOpen(true),
    selectExpression: (expressionId) => actions.onSelectExpression(expressionId),
  });
}
